import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { SearchableCheckbox, Label, Notification } from 'shared/components';
import {
  platformFeatures,
  defaultDisabledFeatureMessage,
} from 'shared/constants';
import {
  isFeatureEnabled,
  isFeatureAvailable,
  mapFeaturesToSelector,
} from 'shared/helpers';
import ProductFeaturesEditor from 'CreateOrderForm/components/SelectProductForm/components/ProductFeaturesEditor';
import './styles.scss';
import { useTranslation } from 'react-i18next';

const ProductFeaturesTab = ({
  productFeatures,
  selectedProductFeatures,
  onFeaturesChange,
  showExpiryDate,
}) => {
  const { t } = useTranslation();
  const productFeaturesOptions = mapFeaturesToSelector(productFeatures || []);

  // Internal state for managing features
  const [internalFeatures, setInternalFeatures] = useState(
    selectedProductFeatures,
  );

  // Update internal state when props change
  useEffect(() => {
    setInternalFeatures(selectedProductFeatures);
  }, [selectedProductFeatures]);

  const productFeatureIds = useMemo(
    () => new Set(productFeaturesOptions.map((feature) => feature.value)),
    [productFeaturesOptions],
  );

  const mappedSelectedProductFeatures =
    mapFeaturesToSelector(internalFeatures) || [];

  const selectedProductFeatureOptions = useMemo(
    () =>
      mappedSelectedProductFeatures.filter((selectedFeature) => {
        const selectedFeatureId =
          get(selectedFeature, 'value') || get(selectedFeature, 'data.id');
        return productFeatureIds.has(selectedFeatureId);
      }),
    [mappedSelectedProductFeatures, productFeatureIds],
  );

  const notAvailableMessage = (
    <>
      {t(
        'Licensing features in your applications is not available in your plan.',
      )}
      &nbsp;
      {t('Upgrade to use features licensing.')}
    </>
  );

  const handleSelection = (val) => {
    if (!isFeatureEnabled(platformFeatures.extra_feature_licensing)) {
      Notification('error', t(defaultDisabledFeatureMessage));
      return false;
    }

    // Convert the react-select format back to the original format
    const currentProductSelection = val.map(
      (mappedFeature) => mappedFeature.data || mappedFeature,
    );

    // Merge new selection with existing features that aren't in current product
    const currentProductFeatureIds = new Set(
      (productFeatures || []).map((feature) => feature.id),
    );

    // Keep existing features that are not in current product
    const otherProductFeatures = internalFeatures.filter(
      (feature) =>
        !currentProductFeatureIds.has(
          feature.product_feature?.id || feature.id,
        ),
    );

    // Create a Set of feature IDs from the new selection to avoid duplicates
    const newSelectionFeatureIds = new Set(
      currentProductSelection.map(
        (feature) => feature.product_feature?.id || feature.id,
      ),
    );

    // Only add other product features that aren't already in the new selection
    const uniqueOtherFeatures = otherProductFeatures.filter(
      (feature) =>
        !newSelectionFeatureIds.has(feature.product_feature?.id || feature.id),
    );

    // Combine with new selection
    const updatedFeatures = [
      ...uniqueOtherFeatures,
      ...currentProductSelection,
    ];

    setInternalFeatures(updatedFeatures);
    onFeaturesChange(updatedFeatures);
    return true;
  };

  const handleFeaturesEdit = (val) => {
    setInternalFeatures(val);
    onFeaturesChange(val);
  };

  return (
    <section className="ProductFeaturesTab product-features-section">
      <div className="ProductFeatures-field">
        <div className="ProductFeatures-selector">
          <Label text={t('Select product features')} />
          <SearchableCheckbox
            text={t('Features list')}
            options={productFeaturesOptions}
            value={selectedProductFeatureOptions}
            onChangeCallback={handleSelection}
            onMenuClose={() => {}}
            featureAvailable={isFeatureAvailable(
              platformFeatures.extra_feature_licensing,
            )}
            notAvailableMessage={notAvailableMessage}
          />
        </div>
        <div>
          <ProductFeaturesEditor
            disableFeatureExpiry={!showExpiryDate}
            selectedFeatures={internalFeatures}
            onFeaturesChange={handleFeaturesEdit}
          />
        </div>
      </div>
    </section>
  );
};

ProductFeaturesTab.propTypes = {
  productFeatures: PropTypes.array,
  selectedProductFeatures: PropTypes.array.isRequired,
  onFeaturesChange: PropTypes.func.isRequired,
  showExpiryDate: PropTypes.bool,
};

ProductFeaturesTab.defaultProps = {
  productFeatures: [],
  showExpiryDate: true,
};

export default ProductFeaturesTab;
